import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../api/client';
import ProjectCard from './ProjectCard';
import DomainManager from './DomainManager';
import ApiKeyManager from './ApiKeyManager';
import WafAllowlistGuide from './WafAllowlistGuide';
import Modal from '../Common/Modal';
import Loader from '../Common/Loader';
import ErrorMessage from '../Common/ErrorMessage';
import Pagination from '../Common/Pagination';
import {
    trackDashboardView,
    trackNewProjectClick,
    trackProjectCreated,
    trackProjectCardClick,
} from '../../utils/analytics';

const Dashboard = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [creating, setCreating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await projectsAPI.getAll(currentPage, 10);
            setProjects(response.data.projects);
            setTotalPages(response.data.pagination.pages);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        trackDashboardView();
    }, []);

    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!newProjectName.trim()) return;

        try {
            setCreating(true);
            const response = await projectsAPI.create(newProjectName.trim());
            trackProjectCreated(newProjectName.trim());
            setShowModal(false);
            setNewProjectName('');
            navigate(`/project/${response.data.project._id}`);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create project');
        } finally {
            setCreating(false);
        }
    };

    if (loading) {
        return <Loader message="Loading your projects..." />;
    }

    return (
        <div className="fade-in">
            <style>{`
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 40px;
          gap: 16px;
        }
        
        .dashboard-title {
          font-family: "Agrandir Narrow", "Agrandir", Arial, sans-serif;
          font-size: 38px;
          font-weight: 700;
          font-style: normal;
          margin-bottom: 8px;
          color: var(--text-primary);
        }
        
        .dashboard-subtitle {
          color: var(--text-secondary);
          font-size: 15px;
        }
        
        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 30px;
        }
        
        .modal-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
        
        @media (max-width: 768px) {
          .dashboard-header {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }
          
          .dashboard-title {
            font-size: 28px;
          }
          
          .dashboard-subtitle {
            font-size: 14px;
          }
          
          .projects-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          
          .modal-buttons {
            flex-direction: column;
          }
          
          .modal-buttons button {
            width: 100%;
          }
        }
        
        @media (max-width: 480px) {
          .dashboard-title {
            font-size: 24px;
          }
        }
      `}</style>

            <div className="dashboard-header">
                <div>
                    <h1 className="dashboard-title">My Projects</h1>
                    <p className="dashboard-subtitle">Manage your API load testing projects</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => { trackNewProjectClick(); setShowModal(true); }}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    + New Project
                </button>
            </div>

            {/* Domain verification section for paid users */}
            <DomainManager />

            {/* WAF allowlisting guide for paid users */}
            <WafAllowlistGuide />

            {/* API Key management */}
            <ApiKeyManager />

            {error && <ErrorMessage message={error} onRetry={fetchProjects} />}

            {projects.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📊</div>
                    <h3>No projects yet</h3>
                    <p>Create your first project to start load testing your APIs</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                        style={{ width: 'auto' }}
                    >
                        Create Project
                    </button>
                </div>
            ) : (
                <>
                    <div className="projects-grid">
                        {projects.map((project) => (
                            <ProjectCard
                                key={project._id}
                                project={project}
                                onClick={() => { trackProjectCardClick(project._id); navigate(`/project/${project._id}`); }}
                            />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </>
            )}

            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setNewProjectName('');
                }}
                title="Create New Project"
            >
                <form onSubmit={handleCreateProject}>
                    <div className="input-group">
                        <label htmlFor="projectName">Project Name</label>
                        <input
                            id="projectName"
                            type="text"
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            placeholder="e.g., My API Project"
                            required
                            autoFocus
                        />
                    </div>

                    <div className="modal-buttons">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowModal(false);
                                setNewProjectName('');
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={creating || !newProjectName.trim()}
                        >
                            {creating ? 'Creating...' : 'Create Project'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Dashboard;
